import { Badge } from './ui/badge';
import { ShieldCheck, ShieldAlert, Eye } from 'lucide-react';

interface DefenseResult {
  contentScan?: { clean: boolean; findingCount?: number; threatLevel?: string };
  complianceCheck?: { corrected: boolean };
  ipProtection?: { leaked: boolean; termCount?: number };
}

interface DefenseIndicatorProps {
  defense?: DefenseResult;
  compact?: boolean;
}

export default function DefenseIndicator({ defense, compact = false }: DefenseIndicatorProps) {
  if (!defense) return null;

  const scanClean = defense.contentScan?.clean !== false;
  const compliancePass = defense.complianceCheck?.corrected !== true;
  const ipClean = defense.ipProtection?.leaked !== true;
  const allClean = scanClean && compliancePass && ipClean;

  if (compact && allClean) {
    return (
      <div className="flex items-center gap-1.5 mt-1">
        <ShieldCheck className="w-3 h-3 text-ag-active" />
        <span className="text-xs text-gray-500">Defense: active</span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-1.5 pt-1.5 border-t border-[#3A352F]/50">
      <Eye className="w-3 h-3 text-gray-500" />

      {/* Content Scan */}
      {scanClean ? (
        <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-ag-active text-ag-active">
          scan: clean
        </Badge>
      ) : (
        <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-ag-critical text-ag-critical">
          <ShieldAlert className="w-2.5 h-2.5 mr-0.5" />
          {defense.contentScan?.findingCount || 0} {defense.contentScan?.threatLevel || 'threat'} pattern{(defense.contentScan?.findingCount || 0) !== 1 ? 's' : ''} blocked
        </Badge>
      )}

      {/* Compliance */}
      <Badge variant="outline" className={`text-[10px] py-0 px-1.5 ${
        compliancePass ? 'border-ag-active text-ag-active' : 'border-ag-warning text-ag-warning'
      }`}>
        compliance: {compliancePass ? 'pass' : 'corrected'}
      </Badge>

      {/* IP Protection */}
      <Badge variant="outline" className={`text-[10px] py-0 px-1.5 ${
        ipClean ? 'border-ag-active text-ag-active' : 'border-ag-critical text-ag-critical'
      }`}>
        IP: {ipClean ? 'pass' : `${defense.ipProtection?.termCount || 0} scrubbed`}
      </Badge>
    </div>
  );
}
